import {DriverTimeSheet, EventResourceUse, formatAddress, getAddressLink, Resource, TimeSheet, WorkEvent} from "../types";
import React, {useState} from "react";
import { Button, Col, Container, Modal, Row, Tab, Tabs} from "react-bootstrap";
import {getDate, getDateString, getElapsedHours, getTimeString, checkIsAllowed} from "../util/Helpers";
import CreateTimeEntry from "./CreateTimeEntry";
import ElevatorBadge from "./ElevatorBadge";
import {useSelector} from "react-redux";
import {RootState} from "../redux/reduxTypes";

interface EventViewProps {
    closeModal?: () => void,
    eventToView: WorkEvent,
    timeEntries: (TimeSheet | DriverTimeSheet)[],
    resourcesOnEvent?: EventResourceUse[]
}

const ViewEvent: React.FC<EventViewProps> = ({closeModal,resourcesOnEvent, eventToView, timeEntries}) => {
    const [showModal, setShowModal] = useState(false)
    const [selectedTimesheet, setSelectedTimesheet] = useState<TimeSheet | DriverTimeSheet | undefined>(undefined)
    const [timesheetList, setTimesheetList] = useState<(TimeSheet | DriverTimeSheet)[]> (timeEntries)
    const [renderDriverTime, setRenderDriverTime] = useState(false)
    const user = useSelector((state: RootState) => state.user)
    const canViewAssigned = user.groups.some(element =>
        ['Supervisor', 'Dispatch','Office_Admin','Ownership_Group'].includes(element)
    )


    const handleCloseModal = () => {
        setShowModal(false)
        setSelectedTimesheet(undefined)
        setRenderDriverTime(false)
    }

    const handleShowModal = () => {
        setRenderDriverTime(false)
        setShowModal(true)
    }
    const handleOpenDetails = (timesheet: TimeSheet | DriverTimeSheet) => {
        setSelectedTimesheet(timesheet)
        // Set renderDriverTime based on whether this is a driver timesheet
        setRenderDriverTime('vehicle' in timesheet)
        setShowModal(true)
    }
    const handleCreate = (created_obj? : any) => {
        if(created_obj){
            setTimesheetList([created_obj,...timesheetList])
        }
        setShowModal(false)
    }
    const handleUpdate = (updated_obj?: TimeSheet | DriverTimeSheet) => {
        // console.log(updated_obj)
        if(updated_obj && selectedTimesheet){
            // Find the index of the timesheet that was updated
            const index = timesheetList.findIndex(timesheet => 
                timesheet.id === selectedTimesheet.id
            );

            if(index !== -1) {
                // Create a copy of the timesheetList
                const updatedTimesheetList = [...timesheetList];
                // Replace the old timesheet with the updated one
                updatedTimesheetList[index] = updated_obj;
                // Update the state
                setTimesheetList(updatedTimesheetList);
            }
        }
    }

    const sortedTimesheetList = [...timesheetList].sort((a, b) => {
        const ta = new Date(a.start_date).getTime();
        const tb = new Date(b.start_date).getTime();
        if (isNaN(ta) && isNaN(tb)) return 0;
        if (isNaN(ta)) return 1;
        if (isNaN(tb)) return -1;
        return ta - tb;
    });

    return (
        <Container className={'text-center'}>
            <Tabs defaultActiveKey="details" className="mb-3" justify>
                <Tab eventKey="details" title="Details">
                    <Row className={"my-3 text-center"}>
                        <h4 className={'fw-bold'}>{eventToView.jobsite_client? `${eventToView.jobsite_client} - `:""}{eventToView.event_name}</h4>
                        {eventToView.elevator_event && <div><ElevatorBadge /></div>}
                    </Row>
                    <hr/>
                    <Row className="mb-3">
                        <Col>
                            <p className="fw-bold">Start Date & Time</p>
                            <p> {getDate(eventToView.start_date)}, <strong>{getTimeString(eventToView.start_date)}</strong></p>
                        </Col>
                        <Col>
                            <p className="fw-bold">End Date & Time</p>
                            <p> {getDate(eventToView.end_date)}, <strong>{getTimeString(eventToView.end_date)}</strong></p>
                        </Col>
                    </Row>
                    <hr/>
                    {eventToView.supervisor?
                        (<Row className="mb-3">
                            <Col>
                                <p>
                                    <strong>Supervisor: </strong>{`${eventToView.supervisor.first_name} ${eventToView.supervisor.last_name}`}
                                </p>
                            </Col>
                            <Col>
                                <p><strong>Phone:</strong> {eventToView.supervisor.mobile_number}</p>
                                <p><strong>Email:</strong> <a href={`mailto:${eventToView.supervisor.email}`}>{eventToView.supervisor.email}</a></p>
                            </Col>
                        </Row>)
                        :(
                            <Row>
                                <p>
                                    No Assigned Supervisor
                                </p>
                            </Row>
                        )
                    }

                    <hr/>
                    <Row className="mb-3">
                        <Col className="fw-bold">
                            Origin Address
                        </Col>
                        <Col className="">
                            {eventToView.origin_address? (
                                <a href={getAddressLink(eventToView.origin_address)}>{formatAddress(eventToView.origin_address)}</a>
                            ): <p>No Location Specified</p>
                            }
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col className="fw-bold">
                            Destination Address
                        </Col>
                        <Col className="">
                            {eventToView.destination_address? (
                                <a href={getAddressLink(eventToView.destination_address)}>{formatAddress(eventToView.destination_address)}</a>
                            ): <p>No Location Specified</p>
                            }
                        </Col>
                    </Row>

                    <hr/>
                    <Row>
                        <p className="fw-bold">Description</p>
                        <p>{eventToView.event_description}</p>
                    </Row>
                    <Row>
                        <p className="fw-bold">Notes</p>
                        <p>{eventToView.event_notes}</p>
                    </Row>
                </Tab>
                <Tab eventKey="time" title="Timesheets">
                    {sortedTimesheetList.length > 0 ? (
                        sortedTimesheetList.map((entry, index) => {
                            // Check if this is a driver timesheet
                            const isDriverTimesheet = 'vehicle' in entry;
                            const driverEntry = isDriverTimesheet ? (entry as DriverTimeSheet) : null;

                            return (
                                <React.Fragment key={index}>
                                    <Row className="my-3">
                                        <Col>
                                            Employee: {entry.employee.first_name} {entry.employee.last_name}
                                        </Col>
                                        <Col>
                                            Time In: {getDateString(entry.start_date)} <br/>@ {getTimeString(entry.start_date)}
                                        </Col>
                                        <Col>
                                            Time Out: {getDateString(entry.end_date)} <br/>@ {getTimeString(entry.end_date)}
                                        </Col>
                                        <Col xs="2">
                                            Total: {getElapsedHours(entry.start_date, entry.end_date)}
                                        </Col>
                                        <Col xs="2" className="justify-content-center">
                                            <Button size="sm" onClick={() => handleOpenDetails(entry)}>Update</Button>
                                        </Col>
                                    </Row>
                                    {isDriverTimesheet && driverEntry && (
                                        <Row className="mb-3">
                                            <Col>
                                                <strong>Vehicle:</strong> {driverEntry.vehicle.make} {driverEntry.vehicle.model}
                                            </Col>
                                            <Col>
                                                <strong>Drive Time To:</strong> {driverEntry.drive_time_to_location}
                                            </Col>
                                            <Col>
                                                <strong>Drive Time From:</strong> {driverEntry.drive_time_from_location}
                                            </Col>
                                            <Col>
                                                <strong>Off Truck Time:</strong> {driverEntry.off_truck_time}
                                            </Col>
                                        </Row>
                                    )}
                                    <hr/>
                                </React.Fragment>
                            );
                        })
                    ) : (<p>No Time Logged towards this event...</p>)
                    }
                    <Row className="justify-content-center my-3" xs={4}>
                        {checkIsAllowed(user.groups, ['Driver']) ? (
                            <>
                                <Col className="text-end">
                                    <Button variant="primary" onClick={() => {
                                        setShowModal(true);
                                    }}>
                                        Add Regular Time
                                    </Button>
                                </Col>
                                <Col className="text-start">
                                    <Button variant="success" onClick={() => {
                                        setRenderDriverTime(true);
                                        setShowModal(true);
                                    }}>
                                        Add Driver Time
                                    </Button>
                                </Col>
                            </>
                        ) : (
                            <Button variant="primary" onClick={handleShowModal}>
                                Add time
                            </Button>
                        )}
                    </Row>
                </Tab>

                <Tab eventKey="resources" title="Resources">
                    <p className="fw-bold">Equipment Required</p>
                    <div className="resource-details">
                        {resourcesOnEvent && resourcesOnEvent.length > 0 ? (
                            resourcesOnEvent.map((value, index) => {
                                const resourceObj = value.resource as Resource;
                                return (
                                    <Row className="mb-3 text-start" key={index}>
                                        <Col>
                                            <p><strong>Resource:</strong> {resourceObj.resource_name}</p>
                                        </Col>
                                        <Col>
                                            <p><strong>Quantity:</strong> {value.number_reserved}</p>
                                        </Col>
                                        <Col>
                                            <p><strong>Rate Unit: </strong>{value.unit}</p>
                                        </Col>
                                        {value.unit !== 'unit' && (
                                                        <Col className="me-3">
                                                            <p><strong>Duration:</strong> {value.duration}</p>
                                                        </Col>
                                                    )}
                                    </Row>

                                    // <div key={index} className="resource-item mb-2 p-2 border-bottom">
                                    //     <div className="d-flex flex-wrap">
                                    //         <div className="me-3">
                                    //             <strong>{resourceObj.resource_name}</strong>
                                    //         </div>
                                    //         <div className="me-3">
                                    //             <span className="text-muted">Quantity:</span> {value.number_reserved}
                                    //         </div>
                                    //         {value.unit !== 'unit' && (
                                    //             <div className="me-3">
                                    //                 <span className="text-muted">Duration:</span> {value.duration}
                                    //             </div>
                                    //         )}
                                    //         <div className="me-3">
                                    //             <span className="text-muted">Rate Unit:</span> {value.unit}
                                    //         </div>
                                    //     </div>
                                    // </div>
                                );
                            })
                        ) : (
                            <div className="text-muted">No resources assigned</div>
                        )}
                    </div>
                </Tab>


                {canViewAssigned ? (
                    <Tab eventKey="employees" title="Assigned Employees">
                        {eventToView.employeeuser_set && eventToView.employeeuser_set.length >0 ? (
                            eventToView.employeeuser_set.map((entry, index) => (
                                <Row className="mb-3 text-start" key={index}>
                                    <Col>
                                        <p><strong>Employee:</strong> {entry.first_name} {entry.last_name}</p>
                                    </Col>
                                    <Col>
                                        <p><strong>Phone:</strong> {entry.mobile_number}</p>
                                    </Col>
                                    <Col>
                                        <p><strong>Email:</strong><a href={`mailto:${entry.email}`}> {entry.email}</a></p>
                                    </Col>
                                </Row>
                            ))
                        ): "No Assigned Employees"}
                    </Tab>
                ):<></>}
            </Tabs>
            <Modal show={showModal} onHide={handleCloseModal} size={"lg"}>
                <Modal.Header closeButton>
                    <Modal.Title>Add Time</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {showModal && (
                        <CreateTimeEntry
                            key={`create-time-entry-${renderDriverTime ? 'driver' : 'regular'}`}
                            selectedEvent={eventToView}
                            timeEntryToUpdate={selectedTimesheet && !renderDriverTime ? selectedTimesheet as TimeSheet : undefined}
                            driverTimeEntryToUpdate={selectedTimesheet && renderDriverTime ? selectedTimesheet as DriverTimeSheet : undefined}
                            onCreate={handleCreate}
                            onUpdate={handleUpdate}
                            renderDriver={renderDriverTime}
                            closeModal={handleCloseModal}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}

export default ViewEvent
